import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { AudioPanel } from './components/AudioPanel'
import { PrivacyMeta } from './components/PrivacyMeta'
import { content } from './content/memories'
import type { MemoryCard } from './types'

const sceneEase: [number, number, number, number] = [0.22, 1, 0.36, 1]

type SceneTone = 'dawn' | 'disco' | 'sea' | 'golf' | 'letters' | 'bouquet' | 'finale'

type Scene = {
  id: string
  kicker: string
  title: string
  body: string
  tone: SceneTone
  emoji?: string
  cards?: MemoryCard[]
  note?: string
  blooms?: { id: string; label: string; petalColor?: string }[]
}

export default function App() {
  const reduced = useReducedMotion()
  const scenes = useMemo<Scene[]>(() => {
    const byChapter = (chapter: MemoryCard['chapter']) =>
      content.cards.filter((card) => card.chapter === chapter)

    return [
      {
        id: 'cover',
        kicker: 'for Irina',
        title: content.hero.title,
        body: 'A small moving postcard from one warm weekend: hot dogs, sea air, golf balls, disco light, and kids who felt loved.',
        tone: 'dawn',
        emoji: '✦',
        note: 'from Bolton & Victor, with help from Dad',
      },
      {
        id: 'hotdogs',
        kicker: 'scene one',
        title: 'The hot-dog constellation',
        body: content.chapters.hotdogs.blurb,
        tone: 'disco',
        emoji: content.chapters.hotdogs.emoji,
        cards: byChapter('hotdogs'),
      },
      {
        id: 'beach',
        kicker: 'scene two',
        title: 'Kite Beach, drawn in waves',
        body: content.chapters.beach.blurb,
        tone: 'sea',
        emoji: content.chapters.beach.emoji,
        cards: byChapter('beach'),
      },
      {
        id: 'topgolf',
        kicker: 'scene three',
        title: 'Sunday arcs through the sky',
        body: content.chapters.topgolf.blurb,
        tone: 'golf',
        emoji: content.chapters.topgolf.emoji,
        cards: byChapter('topgolf'),
      },
      {
        id: 'voices',
        kicker: 'small notes',
        title: 'Two little thank-yous, held open',
        body: 'The exact kid words can still be written later. The feeling is already here: you made the weekend feel safe, playful, and special.',
        tone: 'letters',
        emoji: '✉️',
        note: content.kids.map((kid) => `${kid.kid}: ${kid.ideaTags.join(' · ')}`).join('  /  '),
      },
      {
        id: 'bouquet',
        kicker: 'gratitude bouquet',
        title: 'A bloom for every thoughtful thing',
        body: 'Beach plans. Bike rentals. Burgers. Homemade chili. Music. Topgolf. The invisible work of making everyone feel welcome.',
        tone: 'bouquet',
        emoji: '✿',
        blooms: content.bouquet,
      },
      {
        id: 'finale',
        kicker: 'last slide',
        title: content.closing.line,
        body: 'Some weekends are not loud about being important. They just become a little glowing room in memory.',
        tone: 'finale',
        emoji: '♥',
        note: content.closing.signoff,
      },
    ]
  }, [])

  const [index, setIndex] = useState(0)
  const scene = scenes[index]
  const isFirst = index === 0
  const isLast = index === scenes.length - 1

  const go = useCallback(
    (next: number) => {
      setIndex(() => Math.min(Math.max(next, 0), scenes.length - 1))
    },
    [scenes.length],
  )

  const advance = useCallback(() => go(index + 1), [go, index])
  const back = useCallback(() => go(index - 1), [go, index])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === 'PageDown' || event.key === ' ') {
        event.preventDefault()
        go(index + 1)
      }
      if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault()
        go(index - 1)
      }
      if (event.key === 'Home') go(0)
      if (event.key === 'End') go(scenes.length - 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, index, scenes.length])

  return (
    <main className={`slide-deck tone-${scene.tone}`} aria-label="Thank-you slideshow for Irina">
      <PrivacyMeta />
      <AbstractBackdrop tone={scene.tone} reduced={!!reduced} />

      <div className="slide-stage">
        <AnimatePresence mode="wait" initial={false}>
          <motion.section
            key={scene.id}
            aria-labelledby={`${scene.id}-title`}
            className="slide-card"
            initial={reduced ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.985 }}
            animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, y: -18, scale: 0.99 }}
            transition={{ duration: 0.55, ease: sceneEase }}
          >
            <div className="slide-copy">
              <motion.p
                className="slide-kicker"
                initial={reduced ? undefined : { opacity: 0, y: 8 }}
                animate={reduced ? undefined : { opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.08, ease: sceneEase }}
              >
                <span aria-hidden="true">{scene.emoji}</span> {scene.kicker}
              </motion.p>
              <motion.h1
                id={`${scene.id}-title`}
                className="slide-title"
                initial={reduced ? undefined : { opacity: 0, y: 12 }}
                animate={reduced ? undefined : { opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.16, ease: sceneEase }}
              >
                {scene.title}
              </motion.h1>
              <motion.p
                className="slide-body"
                initial={reduced ? undefined : { opacity: 0, y: 12 }}
                animate={reduced ? undefined : { opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.24, ease: sceneEase }}
              >
                {scene.body}
              </motion.p>
              {scene.note && <p className="slide-note">{scene.note}</p>}
            </div>

            <SceneArtwork scene={scene} reduced={!!reduced} />
          </motion.section>
        </AnimatePresence>
      </div>

      <nav className="slide-controls" aria-label="Slideshow controls">
        <button type="button" onClick={back} disabled={isFirst} className="slide-nav-button">
          ← back
        </button>
        <ol className="slide-dots" aria-label="Slides">
          {scenes.map((item, dotIndex) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => go(dotIndex)}
                aria-label={`Go to slide ${dotIndex + 1}: ${item.kicker}`}
                aria-current={dotIndex === index ? 'step' : undefined}
                className="slide-dot"
              />
            </li>
          ))}
        </ol>
        <button type="button" onClick={advance} disabled={isLast} className="slide-nav-button primary">
          {isLast ? 'the end' : 'next →'}
        </button>
      </nav>

      <p className="build-mark">build {__GIT_SHA__}</p>
      <AudioPanel />
    </main>
  )
}

function SceneArtwork({ scene, reduced }: { scene: Scene; reduced: boolean }) {
  if (scene.blooms) return <BloomCloud blooms={scene.blooms} reduced={reduced} />
  if (scene.cards?.length) return <MemoryOrbits cards={scene.cards} tone={scene.tone} reduced={reduced} />

  return (
    <div className="solo-symbol" aria-hidden="true">
      <motion.span
        animate={reduced ? undefined : { rotate: [0, 4, -4, 0], scale: [1, 1.04, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      >
        {scene.emoji ?? '✦'}
      </motion.span>
    </div>
  )
}

function MemoryOrbits({ cards, tone, reduced }: { cards: MemoryCard[]; tone: SceneTone; reduced: boolean }) {
  return (
    <div className="memory-orbits" aria-label="Memory fragments">
      {cards.map((card, i) => (
        <motion.article
          key={card.id}
          className={`memory-fragment fragment-${i % 5}`}
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 18, rotate: -2 }}
          animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, rotate: i % 2 === 0 ? -1.5 : 1.5 }}
          transition={{ duration: 0.55, delay: 0.28 + i * 0.08, ease: sceneEase }}
        >
          <Media card={card} tone={tone} />
          <div className="fragment-copy">
            <p className="fragment-title">{card.title}</p>
            <p>{card.blurb}</p>
            {card.caption && <span>{card.caption}</span>}
          </div>
        </motion.article>
      ))}
    </div>
  )
}

function Media({ card, tone }: { card: MemoryCard; tone: SceneTone }) {
  if (card.video) {
    return (
      <video
        src={card.video.src}
        poster={card.video.poster}
        muted
        playsInline
        controls
        preload="metadata"
        aria-label={card.video.alt}
        className="fragment-media"
      />
    )
  }
  if (card.photo) {
    return <img src={card.photo.src} alt={card.photo.alt} loading="lazy" className="fragment-media" />
  }
  return (
    <div className={`fragment-symbol symbol-${tone}`} aria-hidden="true">
      {card.effect === 'splash'
        ? '〰'
        : card.effect === 'sparkle'
          ? '✦'
          : card.effect === 'flag'
            ? '◒'
            : card.effect === 'topping'
              ? '●'
              : '✿'}
    </div>
  )
}

function BloomCloud({ blooms, reduced }: { blooms: NonNullable<Scene['blooms']>; reduced: boolean }) {
  const count = blooms.length
  return (
    <div className="bloom-flower-wrap" aria-label="Gratitude flower">
      <div className="bloom-flower">
        {blooms.map((bloom, i) => {
          // Distribute around the centre starting at the top (-90° in screen
          // coords) so the first petal reads first when scanned clockwise.
          const angleDeg = (360 / count) * i - 90
          return (
            <motion.div
              key={bloom.id}
              className="bloom-petal"
              style={
                {
                  '--bloom': bloom.petalColor ?? 'var(--color-crimson)',
                  '--petal-angle': `${angleDeg}deg`,
                } as CSSProperties
              }
              initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.4 }}
              animate={reduced ? { opacity: 1 } : { opacity: 1, scale: 1 }}
              transition={{
                duration: 0.55,
                delay: reduced ? 0 : 0.22 + i * 0.06,
                ease: sceneEase,
              }}
            >
              <span className="bloom-petal-label">{bloom.label}</span>
            </motion.div>
          )
        })}
        <motion.div
          className="bloom-heart"
          aria-hidden="true"
          initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.4 }}
          animate={reduced ? { opacity: 1 } : { opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: reduced ? 0 : 0.2, ease: sceneEase }}
        >
          ♥
        </motion.div>
      </div>
    </div>
  )
}

function AbstractBackdrop({ tone, reduced }: { tone: SceneTone; reduced: boolean }) {
  return (
    <div className="abstract-backdrop" aria-hidden="true">
      <motion.div
        className={`orb orb-one tone-${tone}`}
        animate={reduced ? undefined : { x: [0, 28, -10, 0], y: [0, -18, 16, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className={`orb orb-two tone-${tone}`}
        animate={reduced ? undefined : { x: [0, -24, 16, 0], y: [0, 20, -12, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="paper-moon"
        animate={reduced ? undefined : { rotate: [0, 5, -3, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  )
}
